import { VOYAGE_TRACKER_STEP_STATUS } from '~/constants';
import { PORT_STATE } from '~/constants/organisationPortCalls';

import type {
  IVoyageTrackerStep,
  VoyageTrackerEventStatus,
  VoyageTrackerPortType,
} from '~/types';
import type {
  IGetPortLinkParams,
  IPortsCallsPort,
  ISpreadPortCallsParams,
  IUsePortCallsMilestonesParams,
  PortType,
} from '~/types/organisationPortCalls';

import { getDateYear, getTimeFromDate } from './useDates';

const PORT_TYPE_LABELS = {
  L: 'Load Call',
  D: 'Discharge Call',
  F: 'Bunker Call',
} as const;

const getPortLabel = (type: PortType): VoyageTrackerPortType =>
  PORT_TYPE_LABELS[type as keyof typeof PORT_TYPE_LABELS] || 'Port Call';

type PortCallsTerminalEvents = 'eta' | 'etb' | 'etd';

const withMeta = (
  port: IVoyageTrackerStep,
  index: number,
  allPorts: IVoyageTrackerStep[],
): IVoyageTrackerStep => {
  const prev = allPorts[index - 1];
  const first = index === 0;
  const last = index === allPorts.length - 1;
  let { stepStatus } = port;

  if (
    prev &&
    prev.stepStatus === VOYAGE_TRACKER_STEP_STATUS.COMPLETED &&
    stepStatus === VOYAGE_TRACKER_STEP_STATUS.UPCOMING
  ) {
    stepStatus = VOYAGE_TRACKER_STEP_STATUS.ACTIVE;
  }

  return {
    ...port,
    stepStatus,
    hideTopBorder: first,
    hideBottomBorder: last,
  };
};

const getEventName = (
  key: PortCallsTerminalEvents,
  happened: boolean,
): VoyageTrackerEventStatus => {
  if (key === 'eta') return happened ? 'Arrived' : 'Est. Arrival';

  if (key === 'etb') return happened ? 'Berthed' : 'Est. Berth';

  return happened ? 'Departed' : 'Est. Departure';
};

const getPortDisplayType = (port: IPortsCallsPort): VoyageTrackerPortType => {
  if (port.state === 'previous') return 'Previous Port';
  if (port.state === 'next') return 'Next Port';

  const terminalType = port.terminals?.[0]?.type;

  return terminalType && terminalType in PORT_TYPE_LABELS
    ? PORT_TYPE_LABELS[terminalType]
    : 'Port Call';
};

const getPortLink = ({
  portData,
  organisationId,
  router,
  lastVoyagePortId,
}: IGetPortLinkParams): string => {
  if (!portData?.lastVoyagePortId) return '';
  if (!portData.isAgencyPort || portData.state === PORT_STATE.CURRENT)
    return '';
  if (lastVoyagePortId === portData.lastVoyagePortId) return '';

  return router.resolve({
    name: 'OrganisationPortCallsDetailsOverview',
    params: {
      organisationId,
      portId: portData.lastVoyagePortId,
    },
  }).href;
};

const getTerminalEvenStatus = (event) => {
  if (event.happened) return VOYAGE_TRACKER_STEP_STATUS.COMPLETED;
  if (event.isActive) return VOYAGE_TRACKER_STEP_STATUS.ACTIVE;

  return VOYAGE_TRACKER_STEP_STATUS.UPCOMING;
};

const spreadPortCalls = ({
  acc,
  index,
  portData,
  organisationId,
  router,
  lastVoyagePortId,
}: ISpreadPortCallsParams): IVoyageTrackerStep[] => {
  const primaryPort: IVoyageTrackerStep = {
    id: `${portData.portName}-${index}`,
    port: {
      type: getPortDisplayType(portData),
      name: portData.portName,
      swap: true,
      link: getPortLink({
        portData,
        organisationId,
        router,
        lastVoyagePortId,
      }),
    },
    stepType: 'primary',
    padding: index > 0 ? 'extra-large' : 'large',
    stepStatus:
      portData.status === 'active' ? 'activeInProgress' : portData.status,
  };

  const secondaryPorts: IVoyageTrackerStep[] = [];
  const events: PortCallsTerminalEvents[] = ['eta', 'etb', 'etd'];

  portData.terminals.forEach((portCall, index) => {
    if (!portCall) return;

    if (index !== 0) {
      const parentPort: IVoyageTrackerStep = {
        id: `${primaryPort?.port?.name}`,
        port: {
          type: getPortLabel(portCall.type),
          swap: true,
        },
        stepStatus: VOYAGE_TRACKER_STEP_STATUS.UPCOMING,
        padding: 'small',
        stepType: 'secondary',
      };
      secondaryPorts.push(parentPort);

      const allChildrenCompleted = events.every((key) => {
        const event = portCall[key];
        return event?.happened;
      });

      if (allChildrenCompleted) {
        parentPort.stepStatus = VOYAGE_TRACKER_STEP_STATUS.COMPLETED;
      }

      const hasActiveChild = events.some((key) => {
        const event = portCall[key];
        return event?.isActive === true;
      });

      if (hasActiveChild) {
        parentPort.stepStatus = VOYAGE_TRACKER_STEP_STATUS.ACTIVE;
      }
    }

    for (const key of events) {
      const event = portCall[key];
      if (!event) continue;

      secondaryPorts.push({
        id: `${primaryPort?.port?.name}-${key}`,
        port: {
          date: getDateYear(event.date),
          time: getTimeFromDate(event.date),
          eventStatus: getEventName(key, event.happened),
        },

        stepStatus: getTerminalEvenStatus(event),
        padding: 'medium',
        stepType: 'secondary',
      });
    }
  });

  return [...acc, primaryPort, ...secondaryPorts];
};

export const usePortCallsMilestones = ({
  ports,
  organisationId,
  router,
  lastVoyagePortId = '',
}: IUsePortCallsMilestonesParams): IVoyageTrackerStep[] => {
  const milestones = ports
    .reduce<IVoyageTrackerStep[]>((acc, port, index) => {
      if ('state' in port)
        return spreadPortCalls({
          acc,
          index,
          portData: port,
          organisationId,
          router,
          lastVoyagePortId,
        });
      return acc;
    }, [])
    .map(withMeta);

  const disabledFirstPort: IVoyageTrackerStep = {
    id: 'disabled-port',
    port: {
      type: 'Previous Port',
      name: 'None',
      swap: true,
    },
    stepType: 'primary',
    padding: 'large',
    stepStatus: VOYAGE_TRACKER_STEP_STATUS.COMPLETED,
    hideTopBorder: true,
    hideBottomBorder: false,
    disabled: true,
  };

  const disabledLastPort: IVoyageTrackerStep = {
    id: 'disabled-last-port',
    port: {
      type: 'Next Port',
      name: 'None',
      swap: true,
    },
    stepType: 'primary',
    padding: 'large',
    stepStatus: VOYAGE_TRACKER_STEP_STATUS.COMPLETED,
    hideTopBorder: false,
    hideBottomBorder: true,
    disabled: true,
  };

  let result = [...milestones];

  const isDisabledPortsVisible = ports.some((port) => port.state === 'current');

  if (!isDisabledPortsVisible) {
    return result.map((port, index) => withMeta(port, index, result));
  }

  const hasFirstPort = ports.some((port) => port.state === 'previous');
  const hasLastPort = ports.some((port) => port.state === 'next');

  if (!hasFirstPort) result = [disabledFirstPort, ...result];
  if (!hasLastPort) result = [...result, disabledLastPort];

  return result.map((port, index) => withMeta(port, index, result));
};

export const isProduction = import.meta.env?.PROD || false;

export const __test__ = !isProduction
  ? ({
      getEventName,
      withMeta,
      getPortDisplayType,
      spreadPortCalls,
      getPortLink,
    } as const)
  : undefined;
